import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import "../components/Notification/Notification.css";

const NotificationContext = createContext(null);

const icons = {
  success: <FaCheckCircle />,
  error: <FaExclamationCircle />,
  warning: <FaExclamationTriangle />,
  info: <FaInfoCircle />,
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolver = useRef(null);

  const removeNotification = useCallback((id) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  const showNotification = useCallback(
    (message, type = "info", duration = 3500) => {
      const id = `${Date.now()}-${Math.random()}`;

      setNotifications((current) => [
        ...current,
        { id, message, type, duration },
      ]);

      window.setTimeout(() => {
        removeNotification(id);
      }, duration);
    },
    [removeNotification],
  );

  const confirmAction = useCallback(
    ({
      title = "אישור פעולה",
      message = "האם אתם בטוחים?",
      confirmText = "אישור",
      cancelText = "ביטול",
      danger = true,
    } = {}) =>
      new Promise((resolve) => {
        confirmResolver.current = resolve;
        setConfirmState({
          title,
          message,
          confirmText,
          cancelText,
          danger,
        });
      }),
    [],
  );

  const closeConfirm = useCallback((result) => {
    if (confirmResolver.current) {
      confirmResolver.current(result);
      confirmResolver.current = null;
    }
    setConfirmState(null);
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification, confirmAction }}>
      {children}

      <div className="notification-stack" aria-live="polite" aria-atomic="true">
        {notifications.map((item) => (
          <div
            key={item.id}
            className={`site-notification ${item.type}`}
            role="status"
            style={{ "--notification-duration": `${item.duration}ms` }}
          >
            <span className="notification-icon">{icons[item.type] || icons.info}</span>

            <div className="notification-content">
              <strong>
                {item.type === "success"
                  ? "בוצע בהצלחה"
                  : item.type === "error"
                    ? "שגיאה"
                    : item.type === "warning"
                      ? "שימו לב"
                      : "הודעה"}
              </strong>
              <span>{item.message}</span>
            </div>

            <button
              type="button"
              className="notification-close"
              onClick={() => removeNotification(item.id)}
              aria-label="סגירת הודעה"
            >
              <FaTimes />
            </button>

            <span className="notification-progress" />
          </div>
        ))}
      </div>

      {confirmState && (
        <div
          className="confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeConfirm(false);
          }}
        >
          <div className="confirm-dialog" role="dialog" aria-modal="true">
            <div className={`confirm-dialog-icon ${confirmState.danger ? "danger" : "info"}`}>
              {confirmState.danger ? <FaExclamationTriangle /> : <FaInfoCircle />}
            </div>

            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>

            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-cancel"
                onClick={() => closeConfirm(false)}
              >
                {confirmState.cancelText}
              </button>
              <button
                type="button"
                className={confirmState.danger ? "confirm-danger" : "confirm-primary"}
                onClick={() => closeConfirm(true)}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error("useNotification must be used inside NotificationProvider");
  }

  return context;
}
